import { provideZoneChangeDetection } from "@angular/core";
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

// Fix for SockJS "global is not defined" error - must be before any imports that might use SockJS
(window as any).global = window;

import { AppModule } from './app/app.module';

/**
 * Bootstrap the application with delay for HTTP/2 connection pool handling
 *
 * FIN-0072: Promise-based coordination prevents duplicate bootstrap
 * Multiple callers can safely call this function - they'll all await the same bootstrap promise
 */
let bootstrapPromise: Promise<void> | null = null;
async function bootstrapApp(): Promise<void> {
  // If bootstrap is already in progress or complete, return existing promise
  if (bootstrapPromise) {
    console.log('[Bootstrap] Bootstrap already in progress or complete, awaiting existing promise');
    return bootstrapPromise;
  }

  // Start bootstrap and cache the promise
  console.log('[Bootstrap] Bootstrapping Angular application...');
  bootstrapPromise = platformBrowserDynamic()
    .bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()], })
    .then(() => {
      console.log('[Bootstrap] ✅ Angular bootstrap complete');
    })
    .catch((err) => {
      console.error('[Bootstrap] ❌ Angular bootstrap failed:', err);
      // Reset promise on failure to allow retry
      bootstrapPromise = null;
      throw err;
    });

  return bootstrapPromise;
}

// Check if page is being prerendered (Speculation Rules API)
const isPrerendering = (document as any).prerendering;
const isBackgroundTab = document.visibilityState === 'hidden';

if (isPrerendering || isBackgroundTab) {
  // Prerendering or background tab: wait for visibility
  const reason = isPrerendering ? 'prerendering' : 'background tab';
  console.log(`[Bootstrap] Page is ${reason}, delaying app initialization until visible`);
  console.log(`[Bootstrap] Current visibility state: ${document.visibilityState}`);

  // Safety timeout - bootstrap after 15s if visibility never changes
  const timeout = setTimeout(() => {
    console.log(`[Bootstrap] ⚠️  Visibility timeout after 15s - bootstrapping anyway (safety net)`);
    console.log(`[Bootstrap] Final visibility state: ${document.visibilityState}`);
    bootstrapApp();
  }, 15000);

  // Wait for page to become visible
  // FIN-0072: Both prerenderingchange and visibilitychange may fire when prerendering ends,
  // causing duplicate bootstrap. The bootstrapped flag prevents this.
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible' || !(document as any).prerendering) {
      clearTimeout(timeout);
      console.log(`[Bootstrap] Page now visible (was ${reason}), bootstrapping immediately`);
      // FIN-0066: Removed 200ms delay - Tab 1 no longer reconnects while backgrounded
      bootstrapApp();
    } else {
      console.log(`[Bootstrap] Visibility changed but page still hidden, waiting... (${document.visibilityState})`);
    }
  };

  document.addEventListener('prerenderingchange', handleVisibilityChange, { once: true });
  document.addEventListener('visibilitychange', handleVisibilityChange, { once: true });
} else {
  // Visible foreground tab: bootstrap immediately
  // FIN-0066: Removed 200ms delay - Tab 1 no longer reconnects while backgrounded, so no HTTP/2 contention
  console.log('[Bootstrap] Page is visible, bootstrapping immediately');
  bootstrapApp();
}